<!-- @prettier -->
<template>
  <PortalWidget :widget="widget">
    <ValidationObserver
      v-if="!isLoading"
      ref="form"
      slim
    >
      <form
        class="form-horizontal"
        @submit.prevent="updateSettings"
      >
        <div class="container-fluid">
          <fieldset class="form-group">
            <!-- Existing settings -->
            <div
              v-for="setting in settings"
              :key="setting.SettingName"
            >
              <Field
                :label="$t(getTranslationKey(setting.SettingName, 'name'))"
                :has-tooltip="true"
                :tooltip-text="$t(getTranslationKey(setting.SettingName, 'tooltip'))"
              >
                <template #element>
                  <div class="col-sm-2 col-md-1">
                    <nzToggle v-model="setting.SettingValue" />
                  </div>
                </template>
              </Field>
            </div>

            <!-- Shaken Stir section -->
            <Field
              :label="$t('BLOCK_CALLS_SHAKEN_STIR')"
              :has-tooltip="true"
              :tooltip-text="$t('BLOCK_CALLS_SHAKEN_STIR_TT')"
            >
              <template #element>
                <div class="col-md-3">
                  <nzToggle
                    v-model="useShakenStirToBlockCalls"
                    @onToggle="onShakenStirToggle"
                  />
                </div>
              </template>
            </Field>

            <ValidationProvider
              ref="useShakenStir"
              name="shakenStirOption"
              rules="required"
              :vid="'shakenStirOption'"
            >
              <div v-if="useShakenStirToBlockCalls">
                <Field
                  :label="$t('BLOCK_CALLS_SHAKEN_STIR_OPTIONS')"
                  :has-tooltip="true"
                  :tooltip-text="$t('BLOCK_CALLS_SHAKEN_STIR_OPTIONS_TT')"
                >
                  <template #element>
                    <div class="col-md-3">
                      <MultipleToggle
                        :value="selectedShakenStirOption"
                        :options="shakenStirOptions"
                        @onToggle="onSetShakenStirToggle"
                      />
                    </div>
                    <label
                      v-t="'REQUIRED'"
                      class="col-sm-3 col-md-3 form-group__label-required"
                    />
                  </template>
                </Field>
              </div>
            </ValidationProvider>


            <!-- BuyerTags section -->
            <div v-if="isBuyerTagsEnabled">
              <Field
                :label="$t('BUYER_EXTRA_TAGS')"
                :has-tooltip="true"
                :tooltip-text="$t('BUYER_EXTRA_TAGS_TT')"
              >
                <template #element>
                  <div class="col-md-3">
                    <nzToggle
                      v-model="buyerAccessToTagsEnabled"
                      @onToggle="onBuyerTagsSettingStatusChange"
                    />
                  </div>
                </template>
              </Field>
            </div>

            <div v-if="buyerAccessToTagsEnabled">
              <Field
                :label="$t('BUYER_EXTRA_TAGS_OPTIONS')"
                :has-tooltip="true"
                :tooltip-text="$t('BUYER_EXTRA_TAGS_OPTIONS_TT')"
              >
                <template #element>
                  <CheckboxTagsTree
                    :available-tags="availableBuyerExtraTags"
                    :selected-tags="buyerExtraTags"
                    :show-selected="true"
                    :default-classes="false"
                    @update:selectedTags="onSelectedTagsUpdate"
                  />
                </template>
              </Field>
            </div>

            <!-- Jornaya section -->
            <Field
              :label="$t('JORNAYA_SETTINGS')"
              :has-tooltip="true"
              :tooltip-text="$t('JORNAYA_SETTINGS_TT')"
            >
              <template #element>
                <div class="col-md-3">
                  <nzToggle
                    v-model="hasJornayaSettings"
                    @onToggle="onJornayaSettingStatusChange"
                  />
                </div>
              </template>
            </Field>
            <div v-if="hasJornayaSettings">
              <JornayaSettings
                :jornaya-setting="jornayaSettings"
                @jornayaSettingsChange="onJornayaSettingsChange"
              />
            </div>

            <!-- TrustedForm section -->
            <Field
              :label="$t('TRUSTED_FORM_SETTINGS')"
              :has-tooltip="true"
              :tooltip-text="$t('TRUSTED_FORM_SETTINGS_TT')"
            >
              <template #element>
                <div class="col-md-3">
                  <nzToggle
                    v-model="hasTrustedFormSettings"
                    @onToggle="onTrustedFormSettingStatusChange"
                  />
                </div>
              </template>
            </Field>
            <div v-if="hasTrustedFormSettings">
              <TrustedFormSettings
                :trusted-form-setting="trustedFormSettings"
                @trustedFormSettingsChange="onTrustedFormSettingsChange"
              />
            </div>
          </fieldset>
          <div class="form-group">
            <div class="col-sm-9 col-md-9 col-md-offset-3 col-sm-offset-3">
              <PrimaryButton
                type="submit"
                size-class="btn-lg"
                :disabled="!isFormValid"
                :show-loader-on-form="isUpdating"
              >
                {{ $t('ACCOUNT_SETTINGS_PAGE_UPDATE_BUTTON') }}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </form>
    </ValidationObserver>
    <div
      v-if="isLoading"
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'ACCOUNT_SETTINGS_LOADING'" />
    </div>
  </PortalWidget>
</template>

<script>
import { accountsService, accountSettingsService, utilities, featureFlags, tagService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import Loader from '@/core/components/Loader';
import PortalWidget from '@/core/components/PortalWidget.vue';
import PrimaryButton from '@/core/components/PrimaryButton';
import TrustedFormSettings from '@/core/components/TrustedFormSettings';
import JornayaSettings from '@/core/components/JornayaSettings';
import nzToggle from '@/core/components/nzToggle/nzToggle.vue';
import CheckboxTagsTree from '@/core/components/CheckboxTagsTree/CheckboxTagsTree.vue';
import Field from '@/core/components/LabelAndElement/LabelAndElement';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle.vue';
import _ from 'lodash';
import sharedMessages from '@/core/locales';
import messages from '../locales';

export default {
  name: 'AccountSettings',
  components: {
    CheckboxTagsTree,
    Field,
    Loader,
    MultipleToggle,
    nzToggle,
    PortalWidget,
    PrimaryButton,
    TrustedFormSettings,
    JornayaSettings
  },
  data() {
    return {
      isLoading: true,
      isUpdating: false,
      accountSettings: {},
      settings: [],
      translationString: {
        PublisherAccessToRecording: {
          name: 'ACCOUNT_SETTINGS_PUBLISHER_ACCESS_RECORDING',
          tooltip: 'ACCOUNT_SETTINGS_PUBLISHER_ACCESS_RECORDING_TT'
        },
        BlockCallsIfCapped: {
          name: 'ACCOUNT_SETTINGS_PUBLISHER_BLOCK_CALLS_ON_CAP',
          tooltip: 'ACCOUNT_SETTINGS_PUBLISHER_BLOCK_CALLS_ON_CAP_TT'
        },
        FilterCallsThroughTCPAShield: {
          name: 'ACCOUNT_SETTINGS_FILTER_CALLS_THROUGH_TCPA',
          tooltip: 'ACCOUNT_SETTINGS_FILTER_CALLS_THROUGH_TCPA_TT'
        },
        PlayTCPAWarningMessage: {
          name: 'ACCOUNT_SETTINGS_PLAY_TCPA_MESSAGE',
          tooltip: 'ACCOUNT_SETTINGS_PLAY_TCPA_MESSAGE_TT'
        },
        ReportingUserCanViewAs: {
          name: 'ACCOUNT_SETTINGS_REPORTING_USER_CAN_VIEW_AS',
          tooltip: 'ACCOUNT_SETTINGS_REPORTING_USER_CAN_VIEW_AS_TT'
        },
        UseLegacyDuplicateRouting: {
          name: 'ACCOUNT_SETTINGS_USE_LEGACY_DUPLICATE_ROUTING',
          tooltip: 'ACCOUNT_SETTINGS_USE_LEGACY_DUPLICATE_ROUTING_TT'
        },
        RequirePubsToExposeCallerId: {
          name: 'ACCOUNT_SETTINGS_REQUIRE_PUBS_TO_EXPOSE_CALLER_ID',
          tooltip: 'ACCOUNT_SETTINGS_REQUIRE_PUBS_TO_EXPOSE_CALLER_ID_TT'
        },
        ExposeRTBBidTags: {
          name: 'ACCOUNT_SETTINGS_EXPOSE_RTB_BID_TAGS',
          tooltip: 'ACCOUNT_SETTINGS_EXPOSE_RTB_BID_TAGS_TT'
        }
      },
      useShakenStirToBlockCalls: false,
      selectedShakenStirOption: 0,
      rpcSetting: null,
      shakenStirOptions: accountSettingsService.shakenStirSettingOptions,
      trustedFormSettings: null,
      overrideTrustedFormSettings: false,
      hasTrustedFormSettings: false,
      isTrustedFormReady: true,
      jornayaSettings: null,
      hasJornayaSettings: false,
      isJornayaReady: true,
      buyerAccessToTagsEnabled: false,
      buyerExtraTags: [],
      localBuyerExtraTags: [],
      availableBuyerExtraTags: [],
      tagTypes: [],
      featureFlags,
      jornayaValidationState: {
        isValid: true,
        isDirty: false
      },
      trustedFormValidationState: {
        isValid: true,
        isDirty: false
      }
    };
  },
  computed: {
    isFormValid() {
      return this.isTrustedFormReady &&
             this.isJornayaReady &&
             (!this.useShakenStirToBlockCalls || this.selectedShakenStirOption) &&
             this.isJornayaSettingsValid &&
             this.isTrustedFormSettingsValid;
    },
    isJornayaSettingsValid() {
      return !this.hasJornayaSettings || (this.hasJornayaSettings && this.jornayaValidationState.isValid);
    },
    isTrustedFormSettingsValid() {
      return !this.hasTrustedFormSettings || (this.hasTrustedFormSettings && this.trustedFormValidationState.isValid);
    },
    isBuyerTagsEnabled() {
      return this.featureFlags.isOn('buyer_tags');
    }
  },
  watch: {
    localBuyerExtraTags: {
      handler(newTags) {
        this.buyerExtraTags = newTags.map(tag => `${tag.tagType}:${tag.tagName}`);
      },
      deep: true,
    },
    hasJornayaSettings(newValue) {
      if (newValue) {
        this.jornayaValidationState.isDirty = true;
        // If enabled but no settings, mark as invalid
        if (!this.jornayaSettings) {
          this.jornayaValidationState.isValid = false;
          this.isJornayaReady = false;
        }
      } else {
        // Reset validation state when disabled
        this.jornayaValidationState = {
          isValid: true,
          isDirty: false
        };
        this.isJornayaReady = true;
      }
    },
    hasTrustedFormSettings(newValue) {
      if (newValue) {
        this.trustedFormValidationState.isDirty = true;
        // If enabled but no settings, mark as invalid
        if (!this.trustedFormSettings) {
          this.trustedFormValidationState.isValid = false;
          this.isTrustedFormReady = false;
        }
      } else {
        // Reset validation state when disabled
        this.trustedFormValidationState = {
          isValid: true,
          isDirty: false
        };
        this.isTrustedFormReady = true;
      }
    }
  },
  beforeMount() {
    this.widget = {
      title: this.$t('ACCOUNT_SETTINGS_PAGE_TITLE'),
      iconClass:'typcn typcn-business-card',
      samePageTitle: true
    };
  },
  created() {
    this.loadSettings();
  },
  methods: {
    getTranslationKey(settingName, type) {
      return this.translationString[settingName]?.[type] ??
    (type === 'name' ? settingName : 'ACCOUNT_SETTINGS_PAGE_GENERIC_SETTING_TT');
    },
    async loadSettings() {
      if (this.isBuyerTagsEnabled) {
        await this.loadTagsAndAccountSettings();
      } else {
        await this.loadAccountSettings();
      }
      this.isLoading = false;
    },
    async loadAccountSettings() {
      try {
        const response = await accountSettingsService.loadSettings(accountsService.currentAccountId);
        this.accountSettings = response.data.accountSettings;
        this.processAccountSettings();
      } catch (error) {
        utilities.errorHandler(error);
      }
    },
    async loadTagsAndAccountSettings() {
      try {
        const response = await tagService.getTags(accountsService.currentAccountId);
        this.availableBuyerExtraTags = response.data;
        this.getTagsWithCategory();
        await this.loadAccountSettings();
      } catch (error) {
        utilities.errorHandler(error);
      }
    },
    processAccountSettings() {
      this.settings = [];
      Object.entries(this.accountSettings).forEach(([key, value]) => {
        if (this.isSettingField(key) && key !== 'buyerAccessToTags') {
          this.settings.push({
            SettingName: _.upperFirst(key),
            SettingValue: Boolean(value)
          });
        } else if (key === 'shakenStirAllowedAttestations') {
          this.selectedShakenStirOption = value || '';
          this.useShakenStirToBlockCalls = this.selectedShakenStirOption !== '';
        } else if (key === 'rpcSetting') {
          this.rpcSetting = value !== 'null' ? value : null;
        } else if (key === 'trustedFormSettings') {
          this.trustedFormSettings = value && value !== 'null' ? value : null;
          this.hasTrustedFormSettings = !!this.trustedFormSettings;
        } else if (key === 'jornayaSettings') {
          this.jornayaSettings = value !== 'null' ? value : null;
          this.hasJornayaSettings = !!this.jornayaSettings;
        }
      });

      if ('buyerAccessToTags' in this.accountSettings) {
        const buyerTags = this.accountSettings.buyerAccessToTags;

        if (Array.isArray(buyerTags)) {
          // Keep buyerExtraTags as strings for API
          this.buyerExtraTags = buyerTags;

          // Transform tags into objects for CheckboxTagsTree
          this.localBuyerExtraTags = buyerTags.map(tag => {
            if (typeof tag === 'string') {
              const [tagType, tagName] = tag.split(':');
              return { tagType, tagName };
            }
            return tag;
          });

          // Enable toggle if appropriate
          this.buyerAccessToTagsEnabled = Array.isArray(this.localBuyerExtraTags) && this.localBuyerExtraTags.length > 0;
        }
      }

      if (this.jornayaSettings) {
        this.jornayaValidationState = {
          isValid: true,
          isDirty: true
        };
      }

      if (this.trustedFormSettings) {
        this.trustedFormValidationState = {
          isValid: true,
          isDirty: true
        };
      }

      // Ensure toggle states are correctly set
      this.useShakenStirToBlockCalls = !!this.selectedShakenStirOption;
      this.hasTrustedFormSettings = !!this.trustedFormSettings;
      this.hasJornayaSettings = !!this.jornayaSettings;
    },
    isSettingField(field) {
      const nonAutoDetectFields = ['enabled', 'objectId', 'version', 'shakenStirAllowedAttestations', 'rpcSetting', 'trustedFormSettings', 'jornayaSettings'];
      return !nonAutoDetectFields.includes(field);
    },
    async updateSettings() {
      this.isUpdating = true;
      const sanitizedSettings = this.getSanitizedAccountSettings();
      accountSettingsService.updateSettings(accountsService.currentAccountId, sanitizedSettings)
        .then(() => {
          utilities.showToaster('success', this.$t('ACCOUNT_SETTINGS_PAGE_UPDATE_MESSAGE'));
          this.loadAccountSettings(); // Reload settings after successful update
        })
        .catch(error => {
          utilities.errorHandler(error);
        });

      this.isUpdating = false;
    },
    getSanitizedAccountSettings() {
      const baseSettings = this.settings.map(({ SettingName, SettingValue }) => ({
        SettingName,
        SettingValue
      }));

      const additionalSettings = [
        {
          SettingName: 'ShakenStirAllowedAttestations',
          SettingValue: this.useShakenStirToBlockCalls ? this.selectedShakenStirOption : null
        },
        {
          SettingName: 'TrustedFormSettings',
          SettingValue: this.hasTrustedFormSettings ? this.trustedFormSettings : null
        },
        {
          SettingName: 'JornayaSettings',
          SettingValue: this.hasJornayaSettings ? this.jornayaSettings : null
        }
      ];

      if (this.featureFlags.isOn('rpc')) {
        additionalSettings.push({ SettingName: 'RPCSetting', SettingValue: this.rpcSetting });
      }
      if (this.isBuyerTagsEnabled) {
        additionalSettings.push({ SettingName: 'BuyerAccessToTags', SettingValue: this.buyerAccessToTagsEnabled ? this.buyerExtraTags : [] });
      }

      return [...baseSettings, ...additionalSettings];
    },

    getTagsWithCategory() {
      const tags = _.sortBy(this.availableBuyerExtraTags, ['tagType']);
      if (tags && tags.length > 0) {
        let tagType = tags[tags.length - 1].tagType;
        for (let i = tags.length - 1; i >= 0; i--) {
          if (tags[i].tagType !== tagType) {
            tags.splice(i + 1, 0, { tagType });
            tagType = tags[i].tagType;
          }
          if (i === 0) {
            tags.unshift({ tagType });
          }
        }
      }
      this.tagTypes = tags;
    },
    // Toggles
    async onSetShakenStirToggle(val) {
      this.selectedShakenStirOption = val.value;
      // Trigger validation after setting the value
      if (this.$refs.useShakenStir) {
        await this.$refs.useShakenStir.validate();
      }
    },

    async onShakenStirToggle(value) {
      this.useShakenStirToBlockCalls = value;
      if (!value) {
        this.selectedShakenStirOption = null;
        // Reset validation when toggling off
        if (this.$refs.useShakenStir) {
          await this.$refs.useShakenStir.reset();
        }
      }
    },
    onBuyerTagsSettingStatusChange(value) {
      this.buyerAccessToTagsEnabled = value;
      if (!value) {
        this.buyerExtraTags = [];
        this.localBuyerExtraTags = [];
      }
    },
    onSelectedTagsUpdate(newTags) {
      // newTags will be array of objects with tagType and tagName
      this.localBuyerExtraTags = newTags;
      // Convert to string format for API
      this.buyerExtraTags = newTags.map(tag => `${tag.tagType}:${tag.tagName}`);
    },
    onTrustedFormSettingsChange({ isFormValid, value }) {
      this.trustedFormValidationState.isValid = isFormValid;
      this.trustedFormValidationState.isDirty = true;
      this.isTrustedFormReady = isFormValid;
      this.trustedFormSettings = value;
    },

    onTrustedFormSettingStatusChange(value) {
      this.hasTrustedFormSettings = value;
      if (!value) {
        this.trustedFormSettings = null;
        this.onTrustedFormSettingsChange({ isValid: true, value: null });
      } else if (!this.trustedFormValidationState.isDirty) {
        // Initial enable - trigger validation
        this.onTrustedFormSettingsChange({ isValid: false, value: null });
      }
    },
    onJornayaSettingsChange({ isFormValid, value }) {
      this.jornayaValidationState.isValid = isFormValid;
      this.jornayaValidationState.isDirty = true;
      this.isJornayaReady = isFormValid;
      this.jornayaSettings = value;
    },

    onJornayaSettingStatusChange(value) {
      this.hasJornayaSettings = value;
      if (!value) {
        this.jornayaSettings = null;
        this.onJornayaSettingsChange({ isFormValid: true, value: null });
      } else if (!this.jornayaValidationState.isDirty) {
        // Initial enable - trigger validation
        this.onJornayaSettingsChange({ isFormValid: false, value: null });
      }
    },

  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>
